import type { MyClubsItem } from '@bookclub/shared';

interface ClubCardProps {
  club: MyClubsItem;
  className?: string;
  onClick: () => void;
}

function formatRelativeReadAt(value: string | null): string {
  if (!value) return '';

  const readAt = new Date(value);
  if (Number.isNaN(readAt.getTime())) return '';

  const diffDays = Math.max(0, Math.floor((Date.now() - readAt.getTime()) / 86_400_000));
  if (diffDays <= 0) return 'сегодня';
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) {
    const suffix = diffDays >= 2 && diffDays <= 4 ? 'дня назад' : 'дней назад';
    return `${diffDays} ${suffix}`;
  }
  if (diffDays < 14) return 'неделю назад';

  const weeks = Math.max(2, Math.round(diffDays / 7));
  const suffix = weeks === 2 ? 'недели назад' : 'недель назад';
  return `${weeks} ${suffix}`;
}

export function ClubCard({ club, className = '', onClick }: ClubCardProps) {
  const progressLabel = `${club.completedChapters} из ${club.totalChapters} глав`;
  const lastReadAt = formatRelativeReadAt(club.lastReadAt);
  const compactCoverClass =
    'h-[60px] w-[44px] rounded-[12px] min-[430px]:h-[64px] min-[430px]:w-[48px]';

  return (
    <button
      type="button"
      aria-label={`${club.title}${club.author ? `, ${club.author}` : ''}, ${progressLabel}${lastReadAt ? `, ${lastReadAt}` : ''}`}
      onClick={onClick}
      className={[
        'flex w-full items-center gap-3 px-3.5 py-3.25 text-left transition-transform active:scale-[0.992] min-[430px]:gap-3.5 min-[430px]:px-4 min-[430px]:py-3.5',
        className,
      ].join(' ')}
    >
      <div className="relative shrink-0">
        <div className={['overflow-hidden bg-[linear-gradient(180deg,#f4ead5_0%,#efe3cb_100%)]', compactCoverClass].join(' ')}>
          {club.coverUrl ? (
            <img src={club.coverUrl} alt={club.title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,rgba(255,255,255,0.24),rgba(255,248,231,0.78))] text-[rgba(26,26,26,0.34)]">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
              </svg>
            </div>
          )}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="line-clamp-2 text-[13px] font-black uppercase leading-[1.04] tracking-[0.01em] text-[var(--foreground)] min-[430px]:text-[15px]">
              {club.title}
            </h3>
            {club.author && (
              <p className="mt-0.5 text-[11px] leading-[1.3] text-[rgba(26,26,26,0.62)] min-[430px]:text-[12px]">
                {club.author}
              </p>
            )}
            <p className="mt-0.5 text-[10px] font-medium leading-[1.35] text-[rgba(26,26,26,0.54)] min-[430px]:text-[11px]">
              {progressLabel}
            </p>
          </div>

          {lastReadAt && (
            <p className="shrink-0 pt-0.5 text-right text-[10px] font-medium leading-[1.35] text-[rgba(26,26,26,0.34)] min-[430px]:text-[11px]">
              {lastReadAt}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
